import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Avatar } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';

import { type TypeOrganization } from '../../common/dataTypes';
import { createTanstackTable } from '../../common/utils';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import useUserStore from '../../hooks/zustand/useUserStore';
import TanstackTable from '../TanstackTable';

export type ProjectRow = {
  id: number;
  name: string;
  logo?: string;
  description: string;
  memberCount: number | undefined;
  projectCount: number | undefined;
};

const defaultData: ProjectRow[] = [];

const columnHelper = createColumnHelper<ProjectRow>();

const columns = [
  columnHelper.accessor('name', {
    cell: (info) => (
      <div className="group flex items-center gap-2 text-start">
        <Avatar
          src={info.row.original.logo ?? ''}
          sx={{
            height: 35,
            width: 35,
            rounded: 'full',
          }}
        />
        <p className="w-full">{info.getValue()}</p>
      </div>
    ),
    header: () => (
      <div className="flex items-center gap-2">
        <p>Name</p>
      </div>
    ),
  }),
  columnHelper.accessor('description', {
    cell: (info) => (
      <div className="group line-clamp-2 flex items-center gap-2 text-start">
        <p className="w-full">{info.getValue()}</p>
      </div>
    ),
    header: () => (
      <div className="flex items-center gap-2">
        <p>Description</p>
      </div>
    ),
  }),
  columnHelper.accessor('memberCount', {
    id: 'membersCount',
    cell: (info) => {
      const value = info.getValue();

      return (
        <div className="max-w-[250px] 2xl:max-w-[350px]">{value ?? '--'}</div>
      );
    },
    header: () => <div>Number of members</div>,
  }),
  columnHelper.accessor('projectCount', {
    id: 'projectsCount',
    cell: (info) => {
      const value = info.getValue();

      return (
        <div className="max-w-[250px] 2xl:max-w-[350px] flex justify-between">
          {value ?? '--'}
        </div>
      );
    },
    header: () => <div>Number of projects</div>,
  }),
];

const OrganizationsTable = ({
  defaultOrganizations,
  isLoading,
}: {
  isLoading: boolean;
  defaultOrganizations: TypeOrganization[] | undefined;
}) => {
  const [data, setData] = useState<ProjectRow[]>(defaultData);

  const navigate = useNavigate();

  const setOrgUsers = useUserStore((state) => state.setOrgUsers);

  const { data: user } = useGetUser();

  useEffect(() => {
    if (defaultOrganizations && defaultOrganizations?.length > 0) {
      setData(
        defaultOrganizations.map((org) => ({
          id: org.id,
          name: org.name,
          logo: org.logo,
          description: org.description,
          memberCount: org.member_count,
          projectCount: org.project_count,
        }))
      );
      return;
    }

    setData([]);
  }, [defaultOrganizations]);

  const table = createTanstackTable<ProjectRow>({
    data,
    columns,
  });

  const handleSelectRow = (id: number) => {
    if (!user) {
      return;
    }

    setOrgUsers({
      id: user.id,
      orgId: id,
    });

    navigate(`/${id}/settings`);
  };

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      handleSelect={{
        onClick: handleSelectRow,
      }}
      emptyState={'No available organizations.'}
    />
  );
};

export default OrganizationsTable;
